import { useContext, useEffect, useRef, useState } from 'react';
import { fmtLocalDate, fmtLocalDateTime, toWire } from '../../utils/datetime';
import { ComboSelect } from '../ComboSelect';
import { NotifyContext } from '../notifications/NotificationProvider';

/**
 * Shared click-to-edit grid cell — BO UI Standard v2 §9 "Editable grids — pure
 * grid, in-place editing" (ruling 2026-07-02).
 *
 * **No textbox drawn at rest**: the cell renders as plain text; the editor
 * appears only WHILE editing (on `trigger`: click or double-click) and commits
 * on blur / Enter (`onCommit(newValue)`); Escape reverts.  The editor type
 * matches the data:
 *   • `text`     — free text in place.
 *   • `combo`    — native select over `options` (pick-from-list, e.g. CollectedBy).
 *   • `datetime` — calendar-with-time (`<input type="datetime-local">`); the
 *     resting cell displays via the shared `fmtLocalDateTime` (MM/DD/YYYY HH:mm,
 *     24-h) and the commit value is produced by the shared `toWire` convention
 *     (§14b.6 — never inline date code).
 *   • `date`     — calendar, date-only (`<input type="date">`); the resting cell
 *     displays via the shared `fmtLocalDate` (MM/DD/YYYY) and commits the same
 *     `toWire` string at LOCAL midnight (§14b.6).
 *
 * Required-yet-empty support: `requiredEmpty` adds `req-empty-cell` (yellow
 * tint `--ai-required-bg`) on the resting cell — pair with a red `*`
 * (`.req-star`) on the column header.  Styles live in `theme/boStandard.css`.
 */
export type InlineCellEditorProps = {
  /** Current raw value.  For `datetime` this is the WIRE string (ISO-shaped). */
  value: string | null | undefined;
  /** Editor kind — defaults to `text`. `time` is a native HH:mm picker (e.g.
   *  TimeCollected) — it cannot hold free text, unlike a plain `text` cell. */
  type?: 'text' | 'combo' | 'datetime' | 'date' | 'number' | 'time';
  /** `text` only: a regex (as a string, no delimiters) the committed value must
   *  match when non-empty — e.g. a phone-number shape for CollectorPhone. An
   *  invalid value is rejected (the cell reverts) instead of silently saved. */
  pattern?: string;
  /** Message shown (native browser validation bubble) when `pattern` rejects
   *  the value. */
  patternMessage?: string;
  /** OPTIONAL — formats the RESTING text only (the editor still edits the raw
   *  value), e.g. a price shown as $10.30 like the legacy grid. Default: the
   *  date / datetime formats, else the value as-is. */
  display?: (value: string) => string;
  /** Shown when the cell is EMPTY — at rest (muted, in place of the value) and
   *  as the input's own placeholder while editing. For a cell whose empty value
   *  is not really empty but INHERITED from somewhere else: Test Code's QC
   *  limits fall back to the Sample Parameter's, and the prototype shows that
   *  fallback as the cell's placeholder (v12:648) so a cell says the same thing
   *  whether or not it is being edited. Opt-in: a column that does not set it
   *  renders exactly as before. */
  placeholder?: string;
  /** Options for the `combo` editor. */
  options?: string[];
  /** What starts editing — defaults to `click`. */
  trigger?: 'click' | 'double-click';
  /** Called with the new value when the edit commits (blur / Enter) and the
   *  value actually changed.  For `datetime` the value is the `toWire` string. */
  onCommit: (newValue: string) => void;
  /** Optional: fires on every keystroke WHILE still editing (text/combo only —
   *  does not close the cell), so a caller can stage the value into a batch-edit
   *  buffer live rather than waiting for blur/Enter. `onCommit` still fires as
   *  usual when editing finishes; calling both is harmless (same idempotent
   *  staging logic on the caller's end). */
  onLiveChange?: (value: string) => void;
  /** Yellow required-yet-empty tint on the resting cell (§9). */
  requiredEmpty?: boolean;
  /** Read-only cell — renders text only, never opens an editor. */
  disabled?: boolean;
  /** `combo` only: show the clear "✕". Defaults to `false` — a grid lookup cell
   *  is a pick-from-list (legacy ALPACA XAF lookups have no clear button, and
   *  clearing a required cell like CollectedBy only yields "User not found").
   *  Set `true` on a column whose combo may legitimately be emptied. */
  clearable?: boolean;
  /** `combo` only: also accept a typed value not in the list (ComboSelect
   *  `allowCustom`). Default false. */
  allowCustom?: boolean;
};

/** Wire ISO → the `datetime-local` input value (`YYYY-MM-DDTHH:mm`). */
function wireToInput(wire: string | null | undefined): string {
  if (!wire) return '';
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(wire);
  return m ? `${m[1]}T${m[2]}` : '';
}

/** Wire ISO → the `date` input value (`YYYY-MM-DD`). */
function wireToDateInput(wire: string | null | undefined): string {
  if (!wire) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(wire);
  return m ? m[1]! : '';
}

/**
 * Move the edit cursor to the neighbouring editable cell — arrow keys and Tab
 * (user request 2026-08-01: "when I edit the Numeric result or any other edit
 * cell use up/down/side arrow the cursor move the position of the next cell").
 *
 * Walks the real table DOM from the cell being edited, skipping cells that
 * aren't editable (display-only columns, `disabled` editors), and OPENS the
 * target by clicking its resting `.gedit-cell` — the same path a mouse click
 * takes, so no editor state is duplicated. Coordinates are captured
 * synchronously (before the commit re-renders the row) and resolved on the next
 * tick against the live nodes.
 */
function moveCursor(from: HTMLElement, dRow: number, dCol: number): void {
  const td = from.closest('td');
  const tr = td?.parentElement ?? null;
  const tbody = tr?.parentElement ?? null;
  if (!td || !tr || !tbody) return;
  const startCol = Array.prototype.indexOf.call(tr.children, td);
  const startRow = Array.prototype.indexOf.call(tbody.children, tr);
  if (startCol < 0 || startRow < 0) return;

  setTimeout(() => {
    let r = startRow;
    let c = startCol;
    // Bounded walk — one step per iteration in the requested direction.
    for (let guard = 0; guard < 500; guard++) {
      r += dRow;
      c += dCol;
      const row = tbody.children[r] as HTMLElement | undefined;
      if (!row) return;                                   // past the first/last row
      if (c < 0 || c >= row.children.length) return;      // past the first/last column
      const cell = row.children[c]?.querySelector<HTMLElement>('.gedit-cell:not(.disabled)');
      if (cell) {
        // Open it exactly as a pointer would. A `double-click`-trigger cell
        // ignores the click, so follow with a dblclick — the other kind ignores
        // that one in turn, so one of the two always lands.
        cell.click();
        cell.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
        return;
      }
    }
  }, 0);
}

/**
 * When an OPEN combo cell closes because the user clicked ANOTHER editable grid
 * cell, make sure that cell actually opens. The combo (ComboSelect) closes on
 * `mousedown` via a document listener; unmounting the portal in the middle of the
 * pointer interaction can swallow the native `click` that would have opened the
 * clicked cell, leaving focus stuck on the just-closed combo (user report
 * 2026-09-28: after picking CollectedBy, clicking CollectedDate closed the combo
 * but did not open the date editor). We re-open the target on the next tick — but
 * ONLY if the native click was in fact lost (the target's cell holds no editor
 * yet), so a click that DID land is never double-fired.
 */
function reopenClickedCell(target: Node | null): void {
  const el = target instanceof Element ? target : null;
  const cell = el?.closest<HTMLElement>('.gedit-cell:not(.disabled)');
  const td = cell?.closest('td');
  const tr = td?.parentElement;
  const tbody = tr?.parentElement;
  if (!td || !tr || !tbody) return;
  // Capture the cell's POSITION, not the node — closing the combo can re-render the
  // grid and replace the <td>, so a held node reference would be stale.
  const colIdx = Array.prototype.indexOf.call(tr.children, td);
  const rowIdx = Array.prototype.indexOf.call(tbody.children, tr);
  if (colIdx < 0 || rowIdx < 0) return;
  setTimeout(() => {
    const liveTd = (tbody.children[rowIdx] as HTMLElement | undefined)?.children[colIdx] as HTMLElement | undefined;
    if (!liveTd) return;
    // The native click already opened it → an editor input / combo is present → skip
    // (never double-open a cell whose click DID land).
    if (liveTd.querySelector('input, .cmb-control')) return;
    const rest = liveTd.querySelector<HTMLElement>('.gedit-cell:not(.disabled)');
    if (!rest) return;
    // Open it exactly as a pointer would (click OR dblclick trigger — one lands).
    rest.click();
    rest.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
  }, 0);
}

export function InlineCellEditor({
  value, type = 'text', options = [], trigger = 'click', onCommit, onLiveChange, requiredEmpty, disabled, clearable = false, allowCustom = false,
  placeholder,
  pattern, patternMessage, display,
}: InlineCellEditorProps) {
  // Null-safe (not useNotify): the editor is also rendered where no provider is mounted.
  const notify = useContext(NotifyContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // The grid ROW that hosts this cell — captured when editing starts so a plain
  // commit (Enter / combo pick) can hand focus BACK to the row instead of letting
  // it drop to <body> when the editor unmounts (user report 2026-09-22). NOT used
  // for Tab / arrow steps (moveCursor already moves focus) or blur (focus left).
  const hostRowRef = useRef<HTMLElement | null>(null);
  // The editing wrapper span — used by the outside-click dismiss for combo cells.
  const editorWrapRef = useRef<HTMLSpanElement | null>(null);

  // A COMBO cell has no reliable blur (its control is a <button>), and ComboSelect
  // only watches for outside clicks while its dropdown is OPEN. So when the dropdown
  // is closed but the cell is still editing (e.g. the user toggled the list shut),
  // clicking ANOTHER cell used to open that cell yet leave this combo editor mounted —
  // focus appeared "stuck" on the collector (user report 2026-09-28, screenshot: a
  // closed-dropdown combo lingering while another cell opened). This document listener,
  // active whenever a combo cell is editing, dismisses the editor on any click outside
  // its own cell and the dropdown list, and re-opens the clicked cell.
  useEffect(() => {
    if (!editing || type !== 'combo') return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (editorWrapRef.current?.contains(t)) return;                 // inside this editor's cell
      if (t instanceof Element && t.closest('.cmb-pop-portal')) return; // inside the dropdown list
      setEditing(false);
      reopenClickedCell(t);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editing, type]);

  const restValue =
    display ? display(value ?? '')
    : type === 'datetime' ? fmtLocalDateTime(value)
    : type === 'date'   ? fmtLocalDate(value)
    : (value ?? '');
  // An empty cell with a placeholder shows the placeholder, muted — it is not
  // the cell's value and must not read as one.
  const restText = restValue !== '' || !placeholder
    ? restValue
    : <span className="gedit-ph">{placeholder}</span>;

  const begin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    hostRowRef.current = (e.currentTarget as HTMLElement).closest('tr');
    setDraft(
      type === 'datetime' ? wireToInput(value)
      : type === 'date'   ? wireToDateInput(value)
      : (value ?? ''));
    setEditing(true);
  };

  const commit = (raw: string, refocusRow = false) => {
    // A text value the `pattern` rejects (e.g. a collector phone typed with fewer than
    // 7 digits) used to close the editor and silently revert, so what the user had typed
    // vanished ("data automatically cleared"). Keep the editor open with the typed text
    // and say why instead — Esc still cancels.
    if (type === 'text' && pattern && raw && !new RegExp(`^(?:${pattern})$`).test(raw)) {
      notify?.warning(patternMessage ?? 'The value is not in the expected format.');
      return;
    }
    setEditing(false);
    if (refocusRow && hostRowRef.current) {
      const row = hostRowRef.current;
      // After the editor unmounts and the resting cell re-renders, return focus to
      // the row so keyboard nav (↑/↓, Enter) resumes where the user was.
      setTimeout(() => { if (row.isConnected) row.focus({ preventScroll: true }); }, 0);
    }
    if (type === 'datetime') {
      if (!raw) return;                                  // cleared / untouched — keep as-is
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const wire = toWire(d);
      if (wire !== (value ?? '')) onCommit(wire);
      return;
    }
    if (type === 'date') {
      if (!raw) return;                                  // cleared / untouched — keep as-is
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!m) return;
      // Build LOCAL midnight from the parts — never `new Date('YYYY-MM-DD')`,
      // which parses as UTC and lands on the PREVIOUS day west of Greenwich.
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (Number.isNaN(d.getTime())) return;
      const wire = toWire(d);
      if (wire !== (value ?? '')) onCommit(wire);
      return;
    }
    // A `text` cell with a `pattern` (e.g. CollectorPhone) rejects a value that
    // doesn't match instead of silently saving free text — the cell reverts to
    // its last valid value. Empty is always allowed (mandatory-ness is a
    // separate, `requiredEmpty` concern).
    if (type === 'text' && pattern && raw && !new RegExp(`^(?:${pattern})$`).test(raw)) return;
    if (raw !== (value ?? '')) onCommit(raw);
  };

  // Live equivalent of commit() for date/datetime — emits the parsed WIRE value
  // (not the raw input) so a caller staging on `onLiveChange` gets the SAME value
  // commit produces. Lets a grid enable "Save changes" the moment a date is picked,
  // before blur/Tab (GoLive parity). No-op until the input holds a full valid date.
  const emitLive = (raw: string) => {
    if (!onLiveChange) return;
    if (type === 'datetime') {
      if (!raw) return;
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) onLiveChange(toWire(d));
      return;
    }
    if (type === 'date') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!m) return;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (!Number.isNaN(d.getTime())) onLiveChange(toWire(d));
      return;
    }
    onLiveChange(raw);
  };

  if (editing) {
    const keys = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      const el = e.currentTarget;
      // Commit what's typed, then open the neighbouring editable cell.
      const step = (dRow: number, dCol: number) => {
        e.preventDefault();
        moveCursor(el, dRow, dCol);   // reads the DOM NOW, clicks after the re-render
        commit(el.value);
      };

      if (e.key === 'Enter') { e.preventDefault(); commit(el.value, true); return; }
      if (e.key === 'Escape') { e.preventDefault(); setEditing(false); return; }
      // Tab walks to the next / previous editable cell in the row (every editor
      // type) — at rest no textbox exists, so the browser's own tab order has
      // nothing to land on.
      if (e.key === 'Tab') { step(0, e.shiftKey ? -1 : 1); return; }

      // Arrow keys move BETWEEN cells for plain text only. On a combo the arrows
      // pick an option and on date/datetime they step the focused segment, so
      // hijacking them there would break the editor itself.
      if (type !== 'text') return;
      if (e.key === 'ArrowUp')   { step(-1, 0); return; }
      if (e.key === 'ArrowDown') { step(1, 0); return; }
      // Left / right only once the caret has run off the end of the text, so
      // arrowing THROUGH what you typed still works normally.
      const input = el as HTMLInputElement;
      const caret = input.selectionStart;
      const collapsed = caret !== null && caret === input.selectionEnd;
      if (e.key === 'ArrowLeft'  && collapsed && caret === 0) step(0, -1);
      if (e.key === 'ArrowRight' && collapsed && caret === input.value.length) step(0, 1);
    };
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    return (
      <span ref={editorWrapRef} onClick={stop} onDoubleClick={stop}
        onKeyDown={e => {
          if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); return; }
          // Tab / Shift+Tab out of a COMBO cell: close it and open the neighbouring
          // cell, exactly as Tab does from a text cell. Without this the combo stayed
          // open while focus moved on, so tabbing across a row left several dropdowns
          // open at once (Reagent Prep levels grid, user report 2026-11-04). Its keys
          // arrive here from the portaled list too — React bubbles portal events
          // through the component tree. The pick itself is unchanged (Enter / click).
          if (type === 'combo' && e.key === 'Tab' && editorWrapRef.current) {
            e.preventDefault();
            e.stopPropagation();
            moveCursor(editorWrapRef.current, 0, e.shiftKey ? -1 : 1);
            setEditing(false);
          }
        }}>
        {type === 'combo' ? (
          <ComboSelect
            value={draft}
            options={options}
            onChange={val => { onLiveChange?.(val); commit(val, true); }}
            placeholder={placeholder ?? "N/A"}
            className="gedit-combo"
            clearable={clearable}
            allowCustom={allowCustom}
            autoOpen
            // Clicking elsewhere closes the dropdown WITHOUT a pick — exit edit mode
            // so the resting value shows and a bulk-apply isn't masked by a stale
            // editor (user report 2026-09-25). The document listener above owns
            // re-opening the clicked cell (it also covers the dropdown-CLOSED case
            // ComboSelect can't see), so onClose only exits edit mode here.
            onClose={() => setEditing(false)}
          />
        ) : (
          <input autoFocus
            className={`gedit-editor${(type === 'date' || type === 'datetime' || type === 'time') && !draft ? ' gedit-editor--empty' : ''}`}
            type={type === 'datetime' ? 'datetime-local' : type === 'date' ? 'date' : type === 'time' ? 'time' : type === 'number' ? 'number' : 'text'}
            pattern={type === 'text' ? pattern : undefined}
            title={type === 'text' ? patternMessage : undefined}
            value={draft}
            placeholder={placeholder}
            onChange={e => {
              setDraft(e.target.value);
              // Plain text stages every keystroke; date/datetime emit the parsed
              // WIRE value once the input is a complete date (emitLive), so a caller
              // can enable "Save changes" the instant a date is picked (GoLive parity).
              if (type !== 'datetime' && type !== 'date') onLiveChange?.(e.target.value);
              else emitLive(e.target.value);
            }}
            onBlur={e => commit(e.target.value)}
            onKeyDown={keys} />
        )}
      </span>
    );
  }

  return (
    <span
      className={`gedit-cell${requiredEmpty ? ' req-empty-cell' : ''}${disabled ? ' disabled' : ''}`}
      title={disabled ? undefined : (trigger === 'click' ? 'Click to edit' : 'Double-click to edit')}
      onClick={trigger === 'click' ? begin : e => e.stopPropagation()}
      onDoubleClick={trigger === 'double-click' ? begin : undefined}>
      {restText}
    </span>
  );
}
